/* React hooks */
import {useCallback, useEffect, useRef, useState} from "react";
/* Components */
import useAuth from "../../hooks/useAuth";
import {updateChild} from "../../api/pro/deceasedReq";
import useData from "../../hooks/useData";
import {useLocation, useParams} from "react-router-dom";
import CivilityForm from "../../components/common/input-layout/CivilityForm";
import LastNameForm from "../../components/common/input-layout/LastNameForm";
import FirstnameForm from "../../components/common/input-layout/FirstnameForm";
import FormButtons from "../../components/common/FormButtons";
import CityNameZipSuggest from "../../components/common/input-layout/CityNameZipSuggest";
import DateInput from "../../components/common/input-layout/DateInput";
import {
    checkRequiredFields,
    patternListChoices,
    removeErrorList,
    validateFormInput,
    normalizeZipCode
} from "../../helper/tools";

export default function FormChildInfos(
    {
        legend,
        child,
        setShowModal,
        setNewChild,
        apiChildrenInfos,
        deleteChild,
        apiAdminInfos,
        apiPartnerInfos,
        childrenInfos,
        currentChildIndex,
        setShowAdminModal,
        setShowConfirmChildModal,
        setChildCreatedCount,
        registerSubmitFunction,
        noSubmitButton = false
    }) {
    const thisYear =  new Date().getFullYear();
    const { auth } = useAuth();
    const { id } = useParams();
    const { pathname } = useLocation();
    const token = auth?.token;
    const maxDate = thisYear - 18;
    const [values, setValues] = useState({});
    const fieldsetRef = useRef();
    const civilityRef = useRef();
    const formRef = useRef();
    const lastChildId = useRef();
    const { countryList } = useData();
    const todayFormatted = new Date().toISOString().split('T')[0];

    const selectedCountry = countryList.find(country => country.code === values?.tutorAddress?.country);

    useEffect(() => {
        if (values?.hasLegalTutor && !values?.tutorAddress?.country) {
            setValues(prevValues => ({
                ...prevValues,
                tutorAddress: {
                    ...prevValues?.tutorAddress,
                    country: "FR"
                }
            }));
        }
    }, [values?.hasLegalTutor]);


    const onChange = (e) => {
        let value = e.target.value;
        
        // Convertir en nombre pour les radios
        if (e.target.type === "radio" && (e.target.name === "isMinor" || e.target.name === "isDeceased" || e.target.name === "isConjointResponsible" || e.target.name === "hasLegalTutor")) {
            value = parseInt(value, 10);
        }
        
        if (e.target.type === "checkbox") {
            value = e.target.checked;
        }
        
        setValues(prevValues => ({
            ...prevValues,
            [e.target.name]: value,
            // Si le conjoint est le responsable légal, la question du tuteur légal ne se pose plus.
            ...(e.target.name === "isConjointResponsible" && value === 1 ? { hasLegalTutor: 0 } : {})
        }))
        validateFormInput(e)
    }

    const onChangeTutor = (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setValues(prevValues => ({
            ...prevValues,
            tutor: {
                ...prevValues?.tutor,
                [e.target.name]: value
            }
        }))
        validateFormInput(e)
    }

    const onChangeTutorAddress = (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setValues(prevValues => ({
            ...prevValues,
            tutorAddress: {
                ...prevValues?.tutorAddress,
                [e.target.name]: value
            }
        }))
        validateFormInput(e)
    }


    useEffect(() => {
        // Ne resynchroniser que si le prop `child` désigne un enfant différent (changement de cible
        // via le cycle de création/édition), pas à chaque rechargement de childrenInfos pour le même
        // enfant : sinon une saisie en cours (civilité, adresse du tuteur...) serait effacée si un
        // autre sous-formulaire du wizard s'enregistre avec succès pendant que celui-ci est invalide.
        if (child && child?.id !== lastChildId.current) {
            setValues(child)
            lastChildId.current = child?.id;
        } else if (!child && legend?.startsWith("Création")) {
            // Initialiser les valeurs par défaut pour un nouveau formulaire
            setValues({
                civility: "M",
                title: "",
                lastName: "",
                firstName: "",
                hiddenCivility: false,
                isMinor: 0,
                isDeceased: 0,
                isConjointResponsible: 0,
                hasLegalTutor: 0,
                tutor: {
                    civility: "M"
                },
                tutorAddress: {
                    country: "FR"
                }
            })
        }
    }, [ child, legend ]);

    const handleRemoveModal = (e) => {
        if ( legend.startsWith("Création") || e.target.classList.contains("no-modal-form")) {
            setNewChild(-1)
        }
        setShowModal(false)
    }


    const handleSubmit = useCallback(async (e) => {
        e?.preventDefault?.();
        if (!checkRequiredFields(formRef.current)) {
            return;
        }
        let birthdate;
        if (values?.isMinor != 1) {
            birthdate = null
        } else {
            birthdate = values?.birthDate;
        }
        let body = {
            deceasedId: id,
            id: child?.id || 0,
            title: values?.title,
            civility: values?.isMinor == 1 ? "M" : values?.civility,
            hiddenCivility: values?.isMinor == 1 ? true : (values?.hiddenCivility || false),
            lastName: values?.lastName,
            firstName: values?.firstName,
            isMinor: values?.isMinor == 1 ? true : false,
            birthDate: birthdate,
            isConjointResponsible: values?.isConjointResponsible == 1 ? true : false,
            hasLegalTutor: values?.hasLegalTutor == 1 ? true : false,
            isDeceased: values?.isDeceased == 1 ? true : false,
            tutor: values?.hasLegalTutor == 1 ? {
                civility: values?.tutor?.civility,
                firstName: values?.tutor?.firstName,
                lastName: values?.tutor?.lastName,
                birthName: values?.tutor?.birthName,
                email: values?.tutor?.email,
                phone: values?.tutor?.phone,
                mobile: values?.tutor?.mobile
            } : null,
            tutorAddress: values?.hasLegalTutor == 1 ? {
                address: values?.tutorAddress?.address,
                addressComp: values?.tutorAddress?.addressComp,
                zipcode: normalizeZipCode(values?.tutorAddress?.zipcode),
                city: values?.tutorAddress?.city,
                country: selectedCountry?.code
            } : null,
        }
        await updateChild(token, body)
            .then(response => {
                removeErrorList();
                if (!response?.response?.data?.error) {
                    apiChildrenInfos()
                    apiAdminInfos()
                    if (apiPartnerInfos) {
                        apiPartnerInfos()
                    }

                    if (noSubmitButton) {
                        // Rendu inline dans le wizard : pas de cycle modal, l'étape gère sa propre navigation.
                    } else if (legend.startsWith("Création") && pathname?.includes("infos-complementaires/creer")) {
                        setShowModal(false);
                        setValues({});

                        if (setShowConfirmChildModal && setChildCreatedCount) {
                            setTimeout(() => {
                                setChildCreatedCount(prev => prev + 1);
                                setShowConfirmChildModal(true);
                            }, 300);
                        } else {
                            setTimeout(() => {
                                setNewChild(prev => prev + 1);
                                setShowModal(true);
                            }, 300);
                        }
                    } else if (child?.id && childrenInfos && currentChildIndex !== undefined) {
                        const totalExistingChildren = childrenInfos.length;
                        const nextChildIndex = currentChildIndex + 1;

                        if (nextChildIndex < totalExistingChildren) {
                            setShowModal(false);
                            setTimeout(() => {
                                setNewChild(nextChildIndex + 1);
                                setShowModal(true);
                            }, 300);
                        } else {
                            setShowModal(false);
                            setValues({});
                            setTimeout(() => {
                                setNewChild(totalExistingChildren + 1);
                                setShowModal(true);
                            }, 300);
                        }
                    } else {
                        setShowModal(false)
                    }
                } else {
                    // Afficher les erreurs dans le bon fieldset
                    if (response?.response?.data?.error) {
                        const errorDiv = fieldsetRef.current?.querySelector('div.errors');
                        if (errorDiv) {
                            for (let singleValidation of response.response.data.error) {
                                let spanContent = `<p class="validation-message">${singleValidation}</p>`;
                                errorDiv.insertAdjacentHTML('beforeend', spanContent);
                            }
                        }
                    }
                }
            })
    }, [values, selectedCountry, id, child, legend, pathname, childrenInfos, currentChildIndex, noSubmitButton, apiChildrenInfos, apiAdminInfos, apiPartnerInfos, setShowModal, setNewChild, setShowConfirmChildModal, setChildCreatedCount]);

    useEffect(() => {
        if (registerSubmitFunction) {
            registerSubmitFunction(handleSubmit);
        }
    }, [registerSubmitFunction, handleSubmit]);

    return (
        <form ref={formRef} onSubmit={handleSubmit} method="POST" noValidate>
                <div ref={fieldsetRef}>
                    <fieldset>
                        <legend>{ legend }</legend>
                        {values?.isMinor == 0 &&
                            <div className="form-group">
                                <CivilityForm required={true} onChange={onChange} value={values?.civility}/>
                                <div className="form-input">
                                    <label htmlFor="hiddenCivility">Masquer la civilité</label>
                                    <input
                                        type="checkbox"
                                        name="hiddenCivility"
                                        defaultChecked={values?.hiddenCivility}
                                        onChange={onChange}
                                    />
                                </div>
                            </div>
                        }
                        <div className="form-group">
                            <LastNameForm required={true} onChange={onChange} value={values?.lastName} />
                            <FirstnameForm required={true} onChange={onChange} value={values?.firstName} />
                        </div>
                        <div className="form-group">
                            <div className="form-input">
                                <label htmlFor="isMinor" className="required">Mineur</label>
                                <input type="radio" id="isMinor1" name="isMinor" value={1} required checked={values?.isMinor == 1}
                                       onChange={onChange}/><label htmlFor="isMinor1">Oui</label>
                                <input type="radio" id="isMinor0" name="isMinor" value={0} checked={values?.isMinor == 0}
                                       onChange={onChange}/><label htmlFor="isMinor0">Non</label>
                            </div>
                            <div className="form-input">
                                <label className="required">L'enfant est-il décédé ?</label>
                                <input type="radio" id="isDeceased1" name="isDeceased" value={1} required checked={values?.isDeceased == 1}
                                       onChange={onChange}/><label htmlFor="isDeceased1">Oui</label>
                                <input type="radio" id="isDeceased0" name="isDeceased" value={0} checked={values?.isDeceased == 0}
                                       onChange={onChange}/><label htmlFor="isDeceased0">Non</label>
                            </div>
                        </div>
                        {values?.isMinor == 1 &&
                            <>
                                <div className="form-group">
                                    <div className="form-input">
                                        <label htmlFor="birthDate">Date de naissance</label>
                                        <DateInput min={`${maxDate}-01-01`} max={todayFormatted} name="birthDate"
                                               defaultValue={values?.birthDate}
                                               onChange={onChange}/>
                                    </div>
                                    <span className="max-date">(Min : {maxDate})</span>
                                </div>

                                {values?.isDeceased == 0 &&
                                    <>
                                        <div className="form-input">
                                            <label htmlFor="isConjointResponsible" className="required">Le conjoint est-il le responsable légal
                                                ?</label>
                                            <input type="radio" name="isConjointResponsible" id="isConjointResponsible1" value={1}
                                                   required checked={values?.isConjointResponsible == 1}
                                                   onChange={onChange}/><label htmlFor="isConjointResponsible1">Oui</label>
                                            <input type="radio" name="isConjointResponsible" id="isConjointResponsible0" value={0}
                                                   checked={values?.isConjointResponsible == 0}
                                                   onChange={onChange}/><label htmlFor="isConjointResponsible0">Non</label>
                                        </div>
                                        {values?.isConjointResponsible != 1 &&
                                            <div className="form-input">
                                                <label htmlFor="hasLegalTutor" className="required">Y a-t-il un tuteur légal ?</label>
                                                <input type="radio" name="hasLegalTutor" id="hasLegalTutor1" value={1}
                                                       required checked={values?.hasLegalTutor == 1}
                                                       onChange={onChange}/><label htmlFor="hasLegalTutor1">Oui</label>
                                                <input type="radio" name="hasLegalTutor" id="hasLegalTutor0" value={0}
                                                       checked={values?.hasLegalTutor == 0}
                                                       onChange={onChange}/><label htmlFor="hasLegalTutor0">Non</label>
                                            </div>
                                        }
                                    </>
                                }
                            </>
                        }
                        <div className="errors"></div>
                    </fieldset>

                    {(values?.hasLegalTutor == 1) &&
                        <fieldset>
                            <legend>Coordonnées du tuteur</legend>
                            <div className="form-group">
                                <div className="form-input">
                                    <label htmlFor="civility">Civilité</label>
                                    <select name="civility"
                                            ref={civilityRef}
                                            onChange={onChangeTutor}>
                                        <option name="civility" value="M"
                                                selected={values?.tutor?.civility === "M"}>Monsieur
                                        </option>
                                        <option name="civility" value="MME"
                                                selected={values?.tutor?.civility === "MME"}>Madame
                                        </option>
                                    </select>
                                </div>
                                <LastNameForm required={false} onChange={onChangeTutor} value={values?.tutor?.lastName}/>
                                {values?.tutor?.civility === "MME" &&
                                    <div className="form-input">
                                        <label htmlFor="birthName">Nom de naissance </label>
                                        <input
                                            placeholder="Nom de naissance"
                                            type="text"
                                            name="birthName"
                                            pattern={patternListChoices?.lastname}
                                            defaultValue={values?.tutor?.birthName}
                                            onChange={(e) => { e.target.value = e.target.value.toUpperCase(); onChangeTutor(e); }}/>
                                    </div>
                                }
                                <FirstnameForm required={false} onChange={onChangeTutor} value={values?.tutor?.firstName}/>
                            </div>
                            <div className="form-group">
                                <div className="form-input">
                                    <label htmlFor="address">Adresse</label>
                                    <input
                                        placeholder="Adresse"
                                        type="text"
                                        name="address"
                                        pattern={patternListChoices?.address}
                                        defaultValue={values?.tutorAddress?.address}
                                        onChange={onChangeTutorAddress}/>
                                </div>
                                <div className="form-input">
                                    <label htmlFor="tutorAddressComp">Complément</label>
                                    <input
                                        placeholder="Complément d'adresse"
                                        type="text"
                                        name="addressComp"
                                        pattern={patternListChoices?.address}
                                        defaultValue={values?.tutorAddress?.addressComp}
                                        onChange={onChangeTutorAddress}/>
                                </div>
                                {(values?.tutorAddress?.country === "FR" || !values?.tutorAddress?.country) &&
                                    <CityNameZipSuggest
                                        city={values?.tutorAddress?.city}
                                        zipcode={values?.tutorAddress?.zipcode}
                                        zipField="zipcode"
                                        cityField="city"
                                        onChange={onChangeTutorAddress}
                                        type="tutor"
                                        required={false}
                                    />
                                }
                                {(values?.tutorAddress?.country && values?.tutorAddress?.country !== "FR") &&
                                    <>
                                        <div className="form-input">
                                            <label htmlFor="zipcode">Code postal</label>
                                            <input
                                                placeholder="Code postal"
                                                type="text"
                                                name="zipcode"
                                                pattern={patternListChoices?.zip}
                                                defaultValue={values?.tutorAddress?.zipcode}
                                                onChange={onChangeTutorAddress}/>
                                        </div>
                                        <div className="form-input">
                                            <label htmlFor="city">Ville</label>
                                            <input
                                                placeholder="Ville"
                                                type="text"
                                                name="city"
                                                pattern={patternListChoices?.city}
                                                defaultValue={values?.tutorAddress?.city}
                                                onChange={onChangeTutorAddress}/>
                                        </div>
                                    </>
                                }
                                <div className="form-input">
                                    <label htmlFor="country">Pays</label>
                                    <select
                                        name="country"
                                        value={values?.tutorAddress?.country || "FR"}
                                        onChange={onChangeTutorAddress}>
                                        {
                                            countryList.map((country, index) => (
                                                <option key={index} name="country" value={country?.code}>{country?.name}</option>
                                            ))
                                        }
                                    </select>
                                </div>
                            </div>


                            <div className="form-input">
                                <label htmlFor="email">Email</label>
                                <input
                                    placeholder="Email"
                                    type="email"
                                    name="email"
                                    pattern={patternListChoices?.email}
                                    defaultValue={values?.tutor?.email}
                                    onChange={onChangeTutor}/>
                            </div>
                            <div className="form-input">
                                <label htmlFor="phone">Téléphone fixe</label>
                                <input
                                    placeholder="Téléphone fixe"
                                    type="text"
                                    name="phone"
                                    pattern={patternListChoices?.phone}
                                    defaultValue={values?.tutor?.phone}
                                    onChange={onChangeTutor}/>
                            </div>
                            <div className="form-input">
                                <label htmlFor="mobile">Téléphone mobile</label>
                                <input
                                    placeholder="Téléphone mobile"
                                    type="text"
                                    name="mobile"
                                    pattern={patternListChoices?.phone}
                                    defaultValue={values?.tutor?.mobile}
                                    onChange={onChangeTutor}/>
                            </div>
                        </fieldset>
                    }
                </div>

            {/* Boutons pour le mode création (formulaire dédié ou cycle d'ajout d'enfants) */}
            {!noSubmitButton && !values?.id && (
                <FormButtons handleRemoveModal={handleRemoveModal}/>
            )}

            {/* Boutons pour le mode modification */}
            {values?.id && deleteChild && (
                <div className="buttons-container space-between child-delete-row">
                    <button type="button" className="red-button"
                            onClick={async () => {
                                removeErrorList();
                                const errorMessage = await deleteChild(values?.id);
                                if (errorMessage) {
                                    const errorDiv = fieldsetRef.current?.querySelector('div.errors');
                                    if (errorDiv) {
                                        errorDiv.insertAdjacentHTML('beforeend', `<p class="validation-message">${errorMessage}</p>`);
                                    }
                                }
                            }}>
                        <i className="fa fa-trash"/><span>Supprimer</span>
                    </button>
                    {!noSubmitButton &&
                        <button type="submit" className="green-button">
                            <i className="fa fa-check"/><span>Valider</span>
                        </button>
                    }
                </div>
            )}
        </form>
    )
}